import React from "react";
import { User, Home, Sparkles } from "lucide-react";
import { photoUrl } from "../lib/api";

const genderRing = {
  male: "ring-[#2563EB]/70",
  female: "ring-[#D4A017]/80",
  other: "ring-[#082D4A]/55",
};

function years(m) {
  const y = (s) => (String(s || "").match(/\d{4}/) || [""])[0];
  const b = y(m.birth_date), d = y(m.death_date);
  if (!b && !d) return "";
  return `${b || "?"}${d ? " – " + d : ""}`;
}

// A date entered as about/circa/approx/~/"?" is treated as an estimate.
const EST_RE = /(^\s*(c\.?|ca\.?|abt\.?|approx\.?|about|circa|est\.?|~))|(\?\s*$)/i;
export function isEstimatedDate(v) { return !!v && EST_RE.test(String(v)); }

const VARIANTS = {
  classic: { wrap: "w-[180px] px-4 py-4", avatar: "h-[68px] w-[68px]", icon: 30, name: "font-serif text-[15px] leading-snug", yr: "text-[13px]" },
  compact: { wrap: "w-[148px] px-2.5 py-2.5 flex-row gap-2.5 !items-center text-left", avatar: "h-11 w-11 shrink-0", icon: 20, name: "font-serif text-[13px] leading-tight", yr: "text-[11px]" },
  photo: { wrap: "w-[188px] px-3 pt-5 pb-4", avatar: "h-24 w-24", icon: 40, name: "font-serif text-base leading-snug", yr: "text-[13px]" },
};

export const PersonCard = React.forwardRef(({ member, onClick, active, accent, dragging, variant = "classic", isHome = false, needsInfo = false, faded = false, fadedOpacity = 0.6 }, ref) => {
  const src = photoUrl(member.photo_path);
  const ring = genderRing[member.gender] || genderRing.other;
  const v = VARIANTS[variant] || VARIANTS.classic;
  const compact = variant === "compact";
  const yr = years(member);
  const approx = !!(member.origin_unresolved || member.current_unresolved);
  const estimated = isEstimatedDate(member.birth_date) || isEstimatedDate(member.death_date);

  return (
    <button
      ref={ref}
      onClick={() => onClick(member.id)}
      data-testid={`person-card-${member.id}`}
      title={member.full_name}
      style={faded ? { opacity: fadedOpacity } : undefined}
      className={`group/card relative flex flex-col items-center overflow-hidden rounded-2xl border-2 bg-white text-center shadow-md transition-all duration-200 ${v.wrap} ${
        dragging ? "scale-105 shadow-2xl ring-4 ring-[#082D4A]/40" : "hover:-translate-y-1 hover:shadow-xl hover:!opacity-100"
      } ${active ? "border-[#D4A017] ring-4 ring-[#D4A017]/30" : needsInfo ? "border-dashed border-[#E0B04A]" : "border-[#C3D0DC]"}`}
    >
      {accent && <span className={`absolute top-0 ${compact ? "left-0 h-full w-2" : "inset-x-0 h-2"}`} style={{ backgroundColor: accent }} data-testid={`lineage-accent-${member.id}`} />}
      {isHome && (
        <span data-testid={`home-badge-${member.id}`} className="absolute left-1.5 top-2.5 z-10 flex items-center gap-0.5 rounded-full bg-[#082D4A] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
          <Home size={9} /> You
        </span>
      )}
      <div className={`relative overflow-hidden rounded-full ring-[3px] ring-offset-2 ring-offset-white ${v.avatar} ${ring} ${compact ? "" : "mb-2.5"}`}>
        {src ? (
          <img src={src} alt={member.full_name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#E8EEF3] text-[#94A3B8]">
            <User size={v.icon} />
          </div>
        )}
      </div>
      <div className={compact ? "min-w-0 flex-1" : "w-full"}>
        <div className={`break-words font-bold text-[#082D4A] ${v.name} ${compact ? "truncate" : "line-clamp-2"}`}>{member.full_name}</div>
        {yr ? (
          <div className={`mt-1 flex items-center justify-center gap-1 font-medium text-[#475569] ${v.yr}`}>
            {yr}
            {estimated && !compact && <span className="rounded-full bg-[#E8EEF3] px-1.5 py-0.5 text-[10px] font-medium text-[#64748B]" data-testid={`estimated-tag-${member.id}`}>est.</span>}
          </div>
        ) : needsInfo && !compact ? (
          <div className={`mt-1 flex items-center justify-center gap-1 font-medium text-[#B0850F] ${v.yr}`} data-testid={`needs-info-${member.id}`}>
            <Sparkles size={11} /> Add a date
          </div>
        ) : null}
        {approx && !compact && (
          <div className="mt-1 inline-block rounded-full bg-[#E8EEF3] px-1.5 py-0.5 text-[10px] font-medium text-[#64748B]" data-testid={`approx-tag-${member.id}`}>approx. location</div>
        )}
      </div>
    </button>
  );
});

PersonCard.displayName = "PersonCard";
